import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { TagModule } from 'primeng/tag';
import {
  DataTableComponent,
  PaginationEvent,
} from '@shared/components/data-table/data-table.component';
import {
  FormFieldConfig,
  SearchFormComponent,
  SearchFormData,
} from '@app/shared/components/search-form/search-form.component';
import { PermissionService } from '@app/core/services/permission.service';
import { extractErrorMessage } from '@app/shared/utils/error-handler.util';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import {
  getFulfillmentStatusLabel,
  getFulfillmentStatusSeverity,
} from '@shared/order-status/order-status.util';
import { getStockHoldCategoryLabelKey } from '@app/shared/utils/stock-hold-timeout.util';
import { formatDateTime } from '@shared/utils/date-formatter.util';
import { O2oStuckAlertService } from './services/o2o-stuck-alert.service';
import { O2oStuckAlert, O2oStuckAlertSearchParams } from './o2o-stuck-alert.model';
import { O2O_STUCK_ALERT_COLUMNS } from './o2o-stuck-alert.config';

@Component({
  selector: 'app-o2o-stuck-alert',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DataTableComponent,
    SearchFormComponent,
    TagModule,
  ],
  templateUrl: './o2o-stuck-alert.component.html',
  styleUrl: './o2o-stuck-alert.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class O2oStuckAlertComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly translateService = inject(TranslateService);
  private readonly stuckAlertService = inject(O2oStuckAlertService);
  private readonly permissionService = inject(PermissionService);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);

  canRead = this.permissionService.canDoAction('o2o_orders.stuck_alerts.view');

  tableColumns = O2O_STUCK_ALERT_COLUMNS;

  alerts = signal<O2oStuckAlert[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  rows = 20;
  first = signal(0);
  searchParams = signal<Omit<O2oStuckAlertSearchParams, 'page' | 'size'>>({
    status: 'ACTIVE',
  });

  searchFormConfig = signal<FormFieldConfig[]>(this.buildSearchConfig());

  ngOnInit(): void {
    this.loadAlerts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getStatusSeverity(status: string): 'danger' | 'success' {
    return status === 'ACTIVE' ? 'danger' : 'success';
  }

  getStatusLabel(status: string): string {
    return this.translateService.instant(`o2o.stuck_alert.status.${status.toLowerCase()}`);
  }

  getFulfillmentStatusLabel(value: string): string {
    return this.translateService.instant(getFulfillmentStatusLabel(value));
  }

  getFulfillmentStatusSeverity(value: string): string {
    return getFulfillmentStatusSeverity(value);
  }

  getHoldCategoryLabel(value: string): string {
    return this.translateService.instant(getStockHoldCategoryLabelKey(value));
  }

  getDateTimeLabel(value: string): string {
    return formatDateTime(value);
  }

  onSearch(searchData: SearchFormData): void {
    this.searchParams.set({ ...(searchData as O2oStuckAlertSearchParams) });
    this.first.set(0);
    this.loadAlerts();
  }

  onReset(): void {
    this.searchParams.set({});
    this.first.set(0);
    this.loadAlerts();
  }

  onPageChange(event: PaginationEvent): void {
    this.first.set(event.first || 0);
    this.rows = event.rows || this.rows;
    this.loadAlerts();
  }

  private loadAlerts(): void {
    this.loading.set(true);
    const page = Math.floor(this.first() / this.rows);
    this.stuckAlertService
      .pageAlerts({ page, size: this.rows, ...this.searchParams() })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.alerts.set(result.alerts);
          this.totalRecords.set(result.totalRecords);
          this.loading.set(false);
        },
        error: (error: unknown) => {
          this.loading.set(false);
          this.errorDialogService.showError(
            this.errorTranslatorService.translate(
              extractErrorMessage(error),
              'o2o.stuck_alert.messages.load_error',
            ),
          );
        },
      });
  }

  private buildSearchConfig(): FormFieldConfig[] {
    return [
      {
        key: 'innerOrderNo',
        label: 'o2o.stuck_alert.search.inner_order_no',
        type: 'text',
        placeholder: 'o2o.stuck_alert.search.inner_order_no_placeholder',
        width: '220px',
      },
      {
        key: 'holdCategory',
        label: 'o2o.stuck_alert.search.hold_category',
        type: 'select',
        placeholder: 'o2o.stuck_alert.search.select_category',
        width: '220px',
        showClear: true,
        options: [
          { label: 'o2o.stuck_alert.hold_category.cod_unconfirmed', value: 'COD_UNCONFIRMED' },
          { label: 'o2o.stuck_alert.hold_category.prepaid', value: 'PREPAID' },
          { label: 'o2o.stuck_alert.hold_category.marketplace_cod', value: 'MARKETPLACE_COD' },
        ],
      },
      {
        key: 'status',
        label: 'o2o.stuck_alert.search.status',
        type: 'select',
        placeholder: 'o2o.stuck_alert.search.select_status',
        width: '200px',
        showClear: true,
        options: [
          { label: 'o2o.stuck_alert.status.active', value: 'ACTIVE' },
          { label: 'o2o.stuck_alert.status.resolved', value: 'RESOLVED' },
        ],
      },
    ];
  }
}
